/* eslint-disable jsx-a11y/label-has-associated-control */
import { MouseEvent, forwardRef, useCallback, useEffect, useState } from 'react';

import { useCombobox } from 'downshift';

import { useAutofillDetector, useFocusDetector, useForwardedRefLocally } from '../../hooks';
import { IAutoScroll } from '../../types/auto-scroll';
import { getRequiredLabel } from '../../utils/internal';
import { ActionIcon } from '../action-icon';
import { InputMessage } from '../input-message';
import { PlainIcon } from '../plain-icon';
import {
  IconStyles,
  TextFieldInner,
  TextFieldLabel,
  TextFieldWrapper,
} from '../text-fieldish/text-fieldish.styled';
import { TextFieldState } from '../text-fieldish/types';
import { ITextInputProps } from '../text-input';
import { StyledTextInput } from '../text-input/text-input.styled';

import {
  MainPredictionText,
  OptionsList,
  StyledComboBox,
  StyledOptionButton,
  StyledOptionListItem,
} from './autocomplete-options-dropdown.styled';
import { AutoCompleteDropdownProps, Prediction } from './types';

const DefaultRenderer = ({ mainText, secondaryText }: Prediction) => (
  <>
    <MainPredictionText>{mainText}</MainPredictionText>
    {secondaryText}
  </>
);

const AutocompleteOptionsDropdown = forwardRef<
  HTMLInputElement,
  ITextInputProps & AutoCompleteDropdownProps & IAutoScroll
>(
  (
    {
      // Custom props
      'data-testid': testId,
      label,
      predictions = [],
      predictionRenderer = DefaultRenderer,
      onSelection,
      onInputChange,
      errorMessage,
      noPredictionsFoundText,
      clearTextIconTitle,

      // Native input element props
      'aria-label': ariaLabel,
      autoComplete = 'off',
      autoFocus,
      className,
      disabled,
      onAnimationStart,
      pattern,
      readOnly,
      required,
      value,
      onBlur,
      onFocus,
      autoScrollIntoView = true,
      autoScrollIntoViewAlignBlock = 'nearest',
      autoScrollIntoViewAlignInline = 'nearest',
    },
    forwardedRef,
  ) => {
    const genId = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);
    const wrapperTestId = genId('wrapper');
    const labelTestId = genId('label');
    const [cursor, setCursor] = useState(0);
    const { localRef, refForProps } = useForwardedRefLocally<HTMLInputElement>(forwardedRef);
    const { isAutofilled, handleAnimationStart } = useAutofillDetector(onAnimationStart);
    const { isFocused, handleBlur, handleFocus } = useFocusDetector({
      onBlur,
      onFocus,
      autoScrollIntoView,
      autoScrollIntoViewAlignBlock,
      autoScrollIntoViewAlignInline,
    });

    /** useCombobox documentation: https://github.com/downshift-js/downshift/tree/master/src/hooks/useCombobox */
    const {
      isOpen,
      getLabelProps,
      getMenuProps,
      getInputProps,
      getComboboxProps,
      highlightedIndex,
      getItemProps,
      inputValue: currentValue,
      reset,
    } = useCombobox({
      inputValue: value,
      itemToString: (item: Prediction | null) => (item ? String(item.description) : ''),
      items: predictions || [],
      /** This function controls what happens when an input value is changed manually (via keyDown or clearing via the icon) */
      onInputValueChange: ({ inputValue }) => {
        if (!inputValue?.length) {
          localRef?.current?.focus();
        }
        onInputChange(inputValue || '');
      },
      /** This function controls what happens when one of the options in the dropdown is selected */
      onSelectedItemChange: ({ selectedItem }) => {
        onSelection({ address: selectedItem?.description || '', placeId: selectedItem?.id || '' });
      },
    });

    const handleInputChange = useCallback((e) => {
      setCursor(e?.target?.selectionStart);
    }, []);

    useEffect(() => {
      // Keep the cursor to the previous location so that we can edit
      // the text field without it jumping to the end of the string
      const input = localRef.current;
      if (input) {
        input.setSelectionRange(cursor, cursor);
      }
    }, [localRef, cursor, currentValue]);

    /** Reuse styled logic from TextInput */
    const stateAttrs: { [k in keyof TextFieldState]-?: TextFieldState[k] } = {
      $disabled: disabled || !!readOnly,
      $focused: isFocused,
      $autofilled: isAutofilled,
      $hasValue: !!value,
      $hasError: !!errorMessage,
      $isDate: false,
      $iconCount: 1,
    };

    return (
      <TextFieldWrapper
        style={{ position: 'relative' }}
        className={className}
        data-testid={wrapperTestId}
      >
        <StyledComboBox onAnimationStart={handleAnimationStart} {...getComboboxProps()}>
          <TextFieldInner {...stateAttrs}>
            <TextFieldLabel {...stateAttrs} data-testid={labelTestId} {...getLabelProps()}>
              {getRequiredLabel(label, required)}
            </TextFieldLabel>
            <StyledTextInput
              {...stateAttrs}
              aria-label={ariaLabel}
              autoComplete={false}
              data-private
              data-testid={testId}
              pattern={pattern}
              required={required}
              {...getInputProps({
                ref: refForProps,
                disabled,
                type: 'text',
                onBlur: handleBlur,
                onFocus: handleFocus,
                onChange: handleInputChange,
                autoComplete,
                autoFocus,
              })}
            />
            <IconStyles
              // Prevent clicks to icons within from stealing focus from the
              // input, but still allow keyboard focus
              onMouseDownCapture={(event: MouseEvent) => event.preventDefault()}
              onMouseUpCapture={(event: MouseEvent) => {
                event.preventDefault();
                // Gives focus to the text input after clicking a button. The
                // preventDefault from the mouseDown will prevent icons from
                // stealing focus from the input, but this ensures that the text
                // input ends up with focus even if it didn't start with it
                localRef.current?.focus();
              }}
            >
              {currentValue && (
                <ActionIcon
                  title={clearTextIconTitle}
                  onClick={() => {
                    reset();
                  }}
                  icon={'close'}
                />
              )}
              {!currentValue && <PlainIcon title="search" icon={'search'} />}
            </IconStyles>
          </TextFieldInner>
        </StyledComboBox>
        <OptionsList {...getMenuProps()}>
          {isOpen &&
            currentValue?.length >= 3 &&
            predictions?.map((prediction, index) => (
              <StyledOptionListItem
                data-testid="autocomplete-options-list-item"
                key={`${prediction.id}-${index}`}
                {...getItemProps({
                  item: prediction,
                  index,
                })}
              >
                <StyledOptionButton isFocused={highlightedIndex === index ? true : false}>
                  <bdi>{predictionRenderer(prediction)}</bdi>
                </StyledOptionButton>
              </StyledOptionListItem>
            ))}
          {currentValue && !predictions?.length && (
            <StyledOptionListItem>
              <StyledOptionButton disabled={true}>{noPredictionsFoundText}</StyledOptionButton>
            </StyledOptionListItem>
          )}
        </OptionsList>
        {errorMessage && (
          <InputMessage
            data-testid={'address-autocomplete-dropdown-error-message'}
            id="address-autocomplete-error-message"
            hasError={true}
            disableScreenReaderAlert={false}
          >
            {errorMessage}
          </InputMessage>
        )}
      </TextFieldWrapper>
    );
  },
);

export default AutocompleteOptionsDropdown;
